import React from "react";
import { Box } from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { dtGreen } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";

// ===================================================================
//  ETACard — floating card on map showing ETA countdown or ARRIVED
// ===================================================================

interface ETACardProps {
  etaMinutes: number | null;
  isArrived: boolean;
}

const ETACard: React.FC<ETACardProps> = ({ etaMinutes, isArrived }) => {
  const { translate } = useTranslation(["serviceTracking"]);

  if (!isArrived && etaMinutes == null) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 10,
        backgroundColor: isArrived ? dtGreen.green500 : "rgba(255,255,255,0.96)",
        backdropFilter: "blur(12px)",
        borderRadius: dtGreen.radius,
        padding: isArrived ? "12px 24px" : "10px 20px",
        boxShadow: isArrived
          ? `0 4px 20px rgba(16,185,129,0.4), 0 2px 8px rgba(0,0,0,0.1)`
          : `0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)`,
        border: isArrived ? "none" : `1px solid ${dtGreen.green100}`,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        animation: "etaCardEntrance 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both",
        transition: "all 0.3s ease-out",
      }}
    >
      {isArrived ? (
        <>
          <CheckCircleIcon
            sx={{
              fontSize: "28px",
              color: "#FFFFFF",
              animation: "etaCheckPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both",
            }}
          />
          <Box
            sx={{
              fontFamily: dtGreen.fontMono,
              fontSize: "22px",
              fontWeight: 700,
              color: "#FFFFFF",
              letterSpacing: "1px",
            }}
          >
            {translate("serviceTracking:arrived")}
          </Box>
        </>
      ) : (
        <>
          {/* Pulsing green dot */}
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: dtGreen.green500,
              animation: "livePulse 1.5s ease-in-out infinite",
              flexShrink: 0,
            }}
          />
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <Box
              component="span"
              sx={{
                fontFamily: dtGreen.fontMono,
                fontSize: "28px",
                fontWeight: 700,
                color: dtGreen.green600,
                lineHeight: 1,
                letterSpacing: "-0.5px",
              }}
            >
              ~{etaMinutes}
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: dtGreen.font,
                fontSize: "13px",
                fontWeight: 500,
                color: dtGreen.textSecondary,
                lineHeight: 1,
              }}
            >
              {translate("serviceTracking:minutes")}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default React.memo(ETACard);
